/**
 * Specific Month Picker
 *
 * Year navigation + 12-month grid that lets the user pick any calendar month.
 * Renders inline inside a parent dropdown — the parent owns open/close.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatLocalizedDate } from "../lib/format-localized-date";
import { SpecificMonth } from "../types";

interface SpecificMonthPickerProps {
  locale: string;
  selected?: SpecificMonth;
  onSelect: (year: number, month: number) => void;
}

export function SpecificMonthPicker({ locale, selected, onSelect }: SpecificMonthPickerProps) {
  const currentYear = DateTime.now().year;
  const currentMonth = DateTime.now().month;
  const [year, setYear] = useState(selected?.year ?? currentYear);

  const monthNames = Array.from({ length: 12 }, (_, i) =>
    formatLocalizedDate(DateTime.fromObject({ year, month: i + 1 }), "MMM", locale)
  );

  return (
    <div className="px-2 py-1.5">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          className="p-1 rounded hover:bg-accent"
          aria-label="Previous year"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold">{year}</span>
        <button
          type="button"
          onClick={() => setYear((y) => y + 1)}
          disabled={year >= currentYear}
          className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next year"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {monthNames.map((label, idx) => {
          const month = idx + 1;
          const isFuture = year === currentYear && month > currentMonth;
          const isSelected = selected?.year === year && selected?.month === month;
          return (
            <button
              type="button"
              key={month}
              onClick={() => onSelect(year, month)}
              disabled={isFuture}
              className={cn(
                "text-xs px-2 py-1.5 rounded hover:bg-accent transition-colors",
                isSelected && "bg-primary text-primary-foreground hover:bg-primary/90",
                isFuture && "opacity-30 cursor-not-allowed hover:bg-transparent"
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
